import { shutdown } from "@intercom/messenger-js-sdk";
import * as Sentry from "@sentry/react";
import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { auth } from "../api/firebaseApi";
import { clearPersistedQueries, queryClient } from "../lib/query";
import { lawyerSelectors } from "../redux/lawyer/selectors";
import { resetActiveClientData } from "../redux/client-case/activeClientSlice";
import { resetClientsData } from "../redux/clients/clientsSlice";
import { clearAllDocuments } from "../redux/documents/documentSlice";
import {
  clearExpertLetters,
  clearExpertLettersDrafts,
  resetIndividualData
} from "../redux/individual/individualSlice";
import { resetForeignNationalData } from "../redux/foreignNational/foreignNationalSlice";
import { resetLawyerData } from "../redux/lawyer/lawyerSlice";
import { clearLegalBrief, clearPacket } from "../redux/drafts/draftsSlice";
import {
  MIXPANEL_EVENTS,
  resetMixpanel,
  track
} from "../services/analytics/mixpanelClient";

export type LogoutReason =
  | "manual"
  | "profile_missing"
  | "no_account"
  | "login_error"
  | "impersonation_end";

// Stored in localStorage rather than module state so other tabs, which see the
// shared Firebase sign-out, can tell it apart from an unexpected session loss.
const APP_LOGOUT_AT_KEY = "appLogoutAt";
const APP_LOGOUT_WINDOW_MS = 10_000;

export const markAppInitiatedLogout = () => {
  localStorage.setItem(APP_LOGOUT_AT_KEY, String(Date.now()));
};

export const wasAppInitiatedLogout = () => {
  const at = Number(localStorage.getItem(APP_LOGOUT_AT_KEY));
  return Number.isFinite(at) && Date.now() - at < APP_LOGOUT_WINDOW_MS;
};

export const clearStoredToken = () => {
  localStorage.removeItem("token");
};

export const useLogOut = () => {
  const [isLoadingLogout, setIsLoadingLogout] = useState(false);
  const [errorLogin, setErrorLogin] = useState<string | undefined>(undefined);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const uid = useSelector(lawyerSelectors.selectUid);

  const resetIndivReduxStore = () => {
    dispatch(resetActiveClientData());
    dispatch(resetIndividualData());
    dispatch(clearAllDocuments());
    dispatch(clearExpertLetters());
    dispatch(clearExpertLettersDrafts());
    dispatch(clearLegalBrief());
    dispatch(clearPacket());
  };

  const resetReduxStore = () => {
    dispatch(resetLawyerData());
    dispatch(resetClientsData());
    dispatch(resetForeignNationalData());
    resetIndivReduxStore();
  };

  // `replace` on both paths: the login page is called from `LoginPage` itself
  // when there is no user (which would otherwise stack duplicate `/login`
  // entries), and after a sign-out the authenticated page behind us is not a
  // place Back can meaningfully return to.
  const onSubmitLogOut = async (reason: LogoutReason = "manual") => {
    setIsLoadingLogout(true);
    const user = auth.currentUser;
    if (!user) {
      navigate("/login", { replace: true });
      setIsLoadingLogout(false);
      return;
    }
    try {
      track(MIXPANEL_EVENTS.LOGOUT, { reason });
      Sentry.addBreadcrumb({
        category: "auth",
        message: "logout",
        data: { reason }
      });
      markAppInitiatedLogout();
      await signOut(auth);
      // Forget the cached dashboard data (memory and IndexedDB) before the
      // Redux reset drops the uid the persister is keyed by.
      queryClient.clear();
      await clearPersistedQueries(uid).catch(() => undefined);
      resetReduxStore();
      // Reset Mixpanel identity on logout
      resetMixpanel();
      shutdown();
      clearStoredToken();
      navigate("/login", { replace: true });
    } catch (e: unknown) {
      const errorCode =
        typeof e === "object" && e !== null && "code" in e
          ? String((e as { code?: unknown }).code)
          : "unknown";
      setErrorLogin(errorCode);
    } finally {
      setIsLoadingLogout(false);
    }
  };

  return {
    resetIndivReduxStore,
    resetReduxStore,
    isLoadingLogout,
    onSubmitLogOut,
    errorLogin
  };
};
